import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { Fade } from 'react-awesome-reveal'
import { useMediaQuery } from 'react-responsive'

import styles from './authentication-process.module.scss'
import { FederatedButtons } from '@/components/atomic/FederatedButtons'
import { Button, ButtonHierarchy } from '@/components/styleguide/Button'
import { InternalLink } from '@/components/styleguide/Link'
import {
  STATUS_AI_INTRO,
  STATUS_ALREADY_VERIFIED,
  STATUS_FACEBOOK_LOGIN,
  STATUS_FORGOT_PASSWORD,
  STATUS_GATHER_MOBILE_NUMBER,
  STATUS_GOOGLE_LOGIN,
  STATUS_LOGIN,
  STATUS_NEW_PASSWORD,
  STATUS_PASSWORD_COMPLETE,
  STATUS_PASSWORD_RESET_EMAIL_SENT,
  STATUS_PRE_REGISTER,
  STATUS_REGISTER,
  STATUS_VERIFY,
  STATUS_VERIFY_FROM_SIGN_UP,
} from '@/lib/constants'
import { useExperiment } from '@/lib/hooks/useExperiment'
import { addToDataLayer } from '@/lib/utilities/vehicle'
import { Size } from '@/types/system'

const LogInForm = dynamic(() => import('./LogInForm'), { ssr: false })
const SignUpForm = dynamic(() => import('./SignUpForm'), { ssr: false })
const VerifyFormWrapper = dynamic(
  () => import('@/components/views/AuthenticationProcess/VerifyFormWrapper'),
  {
    ssr: false,
  },
)
const ForgotPasswordForm = dynamic(() => import('./ForgotPasswordForm'), { ssr: false })
const UserProfileBuilder = dynamic(() => import('./UserProfileBuilder'), { ssr: false })
const AiIntro = dynamic(() => import('./AiIntro'), { ssr: false })

export const AuthenticationProcess = ({
  initialStatus,
  hideHeaders,
  isEnquiry,
  storeIsUserAuthenticated,
  storeUserName,
  garagePage,
  username,
  getAccountInfo,
  authPage,
  visitorKey,
  isAuthenticated,
  setUserState = () => {},
  isModal = false,
  setStatusHistory,
  shouldShowBackButton,
  getPreviousStatus,
  includeUserProfileBuilder = true,
  postAnalyticsEvent,
  hideModal,
  hideSecondaryActions = false,
}) => {
  const mobileVersion = useMediaQuery({ query: '(max-width: 992px)' })
  const [experimentGroup] = useExperiment()

  const [status, setStatus] = useState(initialStatus ? initialStatus : STATUS_LOGIN)
  const [userEmail, setUserEmail] = useState('')
  const [userPassword, setUserPassword] = useState('')
  const [userFirstName, setUserFirstName] = useState('')
  const [userLastName, setUserLastName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  useEffect(() => {
    setUserState({
      email: userEmail,
    })
  }, [setUserState, status, userEmail])

  const changeStatus = newStatus => {
    addToDataLayer({
      event: 'authentication_step_change',
      action: `${status} to ${newStatus}`,
    })

    if (hideModal) {
      addToDataLayer({
        event: 'modal-view',
        action: '/' + newStatus.toLowerCase().replace('_', '-'),
      })
    }

    setStatus(newStatus)
    if (setStatusHistory) {
      setStatusHistory(prev => [...prev, newStatus])
    }
  }

  const handleBackButtonClick = () => {
    setStatus(getPreviousStatus())
  }

  const renderForm = () => {
    const commonProps = {
      setStatus: changeStatus,
      status,
      isEnquiry,
      garagePage,
      storeUserName,
      username,
      setUserEmail,
      userEmail,
      setUserPassword,
      userPassword,
      setUserFirstName,
      setUserLastName,
      userFirstName,
      userLastName,
      isLoading,
      setIsLoading,
      setError,
      setInfo,
      getAccountInfo,
      authPage,
      visitorKey,
      storeIsUserAuthenticated,
      mobileVersion,
      hideHeaders,
      includeUserProfileBuilder,
      experimentGroup,
      postAnalyticsEvent,
      hideModal,
      hideSecondaryActions,
    }

    switch (status) {
      case STATUS_AI_INTRO:
        return <AiIntro {...commonProps} />
      case STATUS_REGISTER:
      case STATUS_PRE_REGISTER:
        return <SignUpForm {...commonProps} />
      case STATUS_LOGIN:
        return <LogInForm {...commonProps} />
      case STATUS_GOOGLE_LOGIN:
        return (
          <div className={styles['social-wrap']}>
            <p>You&apos;ve previously logged into LeaseLoco with your Google account.</p>
            <div>
              <FederatedButtons googleOnly={true} />
            </div>
          </div>
        )
      case STATUS_FACEBOOK_LOGIN:
        return (
          <div className={styles['social-wrap']}>
            <p>You&apos;ve previously logged into LeaseLoco with your Facebook account.</p>
            <div>
              <FederatedButtons facebookOnly={true} />
            </div>
          </div>
        )
      case STATUS_VERIFY:
      case STATUS_VERIFY_FROM_SIGN_UP:
      case STATUS_ALREADY_VERIFIED:
        return <VerifyFormWrapper {...commonProps} />
      case STATUS_FORGOT_PASSWORD:
      case STATUS_PASSWORD_RESET_EMAIL_SENT:
      case STATUS_NEW_PASSWORD:
      case STATUS_PASSWORD_COMPLETE:
        return <ForgotPasswordForm {...commonProps} />
      case STATUS_GATHER_MOBILE_NUMBER:
        return <UserProfileBuilder {...commonProps} />
      default:
        return <LogInForm {...commonProps} />
    }
  }

  if (isAuthenticated && authPage) {
    return (
      <div className={styles['logged-in']}>
        <div>
          <h2>You&apos;re already logged in.</h2>
          <p>Check out our hot deals</p>
        </div>
        <InternalLink
          hasBackground
          hasPadding
          href="/car-leasing/search"
          hierarchy={ButtonHierarchy.Filled}
          size={Size.Medium}>
          Continue
        </InternalLink>
      </div>
    )
  }

  return (
    <div
      className={`${styles['form-container']} ${isModal && status !== STATUS_AI_INTRO ? styles.modal : ''} ${
        isEnquiry ? styles['form-container--enquiry'] : ''
      } ${hideHeaders ? styles['hide-headers'] : ''}`}>
      {shouldShowBackButton && isModal && (
        <Button
          size={Size.Small}
          onClick={handleBackButtonClick}
          className={styles['back-button']}
          hierarchy={ButtonHierarchy.BezeledGray}
          icon="ChevronLeft"
        />
      )}
      {error && (
        <Fade>
          <p
            className={styles['error-message']}
            role="alert">
            {error}
          </p>
        </Fade>
      )}
      {renderForm()}
      {info && (
        <Fade>
          <p
            className={styles['info-message']}
            role="alert">
            {info}
          </p>
        </Fade>
      )}
    </div>
  )
}

export default AuthenticationProcess
